import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Checkbox, Chip, CircularProgress, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup, Tooltip,
  Menu, MenuItem,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import {
  getConfigStatus, refreshConfigRecorders, getConfigRefreshJob,
  enableConfigRecorders, disableConfigRecorders, deleteConfigRecorders,
  startConfigRolesScan, getConfigRolesJob, fixConfigRoles, getSecurityHubConfig,
} from '../../../services/securityhubApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalErrorState, PortalSectionHeader,
} from '../../../components/portal/PortalStates';
import RolePermissionsPanel from '../../../components/portal/securityhub/RolePermissionsPanel';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import { formatStatusLabel } from '../../../utils/statusLabel';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, listCardSx, tableHeadRowSx, headCellSx, rowSx,
  bodyCellSx, statusChipSx, intentFromColor, emptyTextSx, actionRowSx,
  dialogProps, dialogTitleSx,
} from '../portalStyles';

// AWS commercial regions offered by the "Add region" picker (opt-in / GovCloud /
// China regions need separate enablement and are intentionally not listed).
const AWS_REGIONS = [
  'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2', 'ca-central-1',
  'eu-west-1', 'eu-west-2', 'eu-west-3', 'eu-central-1', 'eu-central-2',
  'eu-north-1', 'eu-south-1', 'eu-south-2',
  'ap-south-1', 'ap-south-2', 'ap-southeast-1', 'ap-southeast-2',
  'ap-southeast-3', 'ap-southeast-4', 'ap-northeast-1', 'ap-northeast-2',
  'ap-northeast-3', 'ap-east-1', 'sa-east-1', 'me-south-1', 'me-central-1',
  'af-south-1',
];

const STATUS_COLOR = {
  enabled: 'success', enabling: 'info', disabling: 'warning', removing: 'warning',
  disabled: 'default', failed: 'error', external: 'secondary', none: 'default',
};
const ACTIVE = ['enabling', 'disabling', 'removing'];
const SETTLED = ['enabled', 'failed'];

// The account column stays pinned while the region columns scroll sideways —
// without it the checkboxes on the right are unreachable in wide orgs.
const stickyHeadCellSx = {
  ...headCellSx,
  position: 'sticky',
  left: 0,
  zIndex: 3,
  backgroundColor: CK.pageBg,
};

const stickyBodyCellSx = {
  ...bodyCellSx,
  position: 'sticky',
  left: 0,
  zIndex: 1,
  backgroundColor: CK.surface,
  fontWeight: 600,
  transition: 'background-color 0.2s ease',
};

const relTime = (iso) => {
  if (!iso) return 'never';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

const cellKey = (r) => `${r.accountId}|${r.region}`;

export default function ConfigRecorders() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [regions, setRegions] = useState([]);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [scope, setScope] = useState('minimal');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [refreshJob, setRefreshJob] = useState(null);
  // Regions the operator added on top of the Security Hub linking regions (kept
  // client-side until a recorder exists there, after which the backend returns
  // them as columns on its own).
  const [extraRegions, setExtraRegions] = useState([]);
  const [addAnchor, setAddAnchor] = useState(null);
  const [enablingKeys, setEnablingKeys] = useState([]);
  const pollRef = useRef(null);
  const refreshPollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([getConfigStatus(), getSecurityHubConfig()]);
      const recs = s?.data?.recorders || [];
      setRows(recs);
      setLastRefreshed(s?.data?.lastRefreshedAt || null);
      try { setRegions(JSON.parse(c?.data?.linkingRegions || '[]')); } catch (_) { setRegions([]); }
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll while any cell is mid-operation.
  useEffect(() => {
    const active = rows.some((r) => ACTIVE.includes(r.status));
    if (active && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const s = await getConfigStatus().catch(() => null);
        if (s) {
          setRows(s.data?.recorders || []);
          setLastRefreshed(s.data?.lastRefreshedAt || null);
        }
      }, POLL_INTERVAL_MS);
    }
    if (!active && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [rows]);

  // Clear the refresh-job poll on unmount so it can't setState on an unmounted component.
  useEffect(() => () => {
    if (refreshPollRef.current) { clearInterval(refreshPollRef.current); refreshPollRef.current = null; }
  }, []);

  // Auto-clear enablingKeys once all keys have reached a settled status.
  useEffect(() => {
    if (enablingKeys.length === 0) return;
    const rowsByKey = Object.fromEntries(rows.map((r) => [cellKey(r), r]));
    const allSettled = enablingKeys.every((k) => SETTLED.includes(rowsByKey[k]?.status));
    if (allSettled) setEnablingKeys([]);
  }, [rows, enablingKeys]);

  const onRefresh = async () => {
    try {
      const res = await refreshConfigRecorders();
      const job = res?.data;
      setRefreshJob(job);
      if (job?.id) {
        refreshPollRef.current = setInterval(async () => {
          const jr = await getConfigRefreshJob(job.id).catch(() => null);
          const next = jr?.data;
          if (!next) return;
          if (next.status !== 'running') {
            clearInterval(refreshPollRef.current); refreshPollRef.current = null;
            setRefreshJob(null);
            await load();
          } else {
            setRefreshJob(next);
          }
        }, POLL_INTERVAL_MS);
      }
    } catch (e) { setError(e.message); }
  };

  const accounts = [...new Set(rows.map((r) => r.accountId))];
  const byKey = Object.fromEntries(rows.map((r) => [cellKey(r), r]));
  const settledCount = enablingKeys.filter((k) => SETTLED.includes(byKey[k]?.status)).length;
  // Columns = Security Hub linking regions / regions the server already returned
  // (via gridRegions) ∪ regions the operator added in this session.
  const displayRegions = [...new Set([...regions, ...rows.map((r) => r.region), ...extraRegions])];
  // Selected-cell statuses gate the action buttons: you can only Disable a
  // recorder that is currently enabled, and only Delete one that actually exists.
  const selectedStatuses = [...selected].map((k) => byKey[k]?.status || 'none');
  const canDisable = selectedStatuses.some((st) => st === 'enabled');
  const canDelete = selectedStatuses.some((st) => ['enabled', 'disabled', 'failed'].includes(st));

  const toggle = (acct, region) => {
    const k = `${acct}|${region}`;
    const cell = byKey[k];
    if (cell && cell.status === 'external') return;
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  };

  const selectedCells = () => [...selected].map((k) => {
    const [accountId, region] = k.split('|');
    return { accountId, region };
  });

  const act = async (fn) => {
    try {
      await fn(selectedCells());
      setSelected(new Set());
      await load();
    } catch (e) { setError(e.message); }
  };

  const refreshing = !!refreshJob;
  const unusedRegions = AWS_REGIONS.filter((r) => !displayRegions.includes(r));

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
      <PortalPageHeader
        icon={<StorageIcon sx={{ fontSize: 24 }} />}
        title="AWS Config (recording)"
        subtitle={<>Records resource configuration so standards can evaluate {' • '}Last refreshed: <b>{relTime(lastRefreshed)}</b></>}
        action={(
          <Button variant="contained" startIcon={<RefreshIcon />} disabled={refreshing} onClick={onRefresh}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
        )}
      />
      </motion.div>

      {refreshing && (
        <motion.div variants={sectionVariants}>
          <OperationProgress percent={refreshJob?.progress} label={refreshJob?.progressLabel || 'Reconciling AWS Config…'} sx={{ mb: 2 }} />
        </motion.div>
      )}
      {enablingKeys.length > 0 && settledCount < enablingKeys.length && (
        <motion.div variants={sectionVariants}>
          <OperationProgress percent={settledCount * 100 / enablingKeys.length} label="Enabling AWS Config…" sx={{ mb: 2 }} />
        </motion.div>
      )}
      {error && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={load} />
        </motion.div>
      )}

      {loading ? (
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading AWS Config recorders…" />
          </Paper>
        </motion.div>
      ) : (
        <>
          <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
            <PortalSectionHeader
              icon={<TuneIcon sx={{ fontSize: 20 }} />}
              title="Recording actions"
              subtitle={`${selected.size} cell${selected.size === 1 ? '' : 's'} selected`}
              mb={2}
            />
            <Box sx={actionRowSx}>
              <ToggleButtonGroup size="small" exclusive value={scope} onChange={(_, v) => v && setScope(v)}>
                <ToggleButton value="minimal">Minimal scope</ToggleButton>
                <ToggleButton value="all">Record all</ToggleButton>
              </ToggleButtonGroup>
              <Button size="small" variant="contained" disabled={selected.size === 0} onClick={() => { setEnablingKeys([...selected]); act((cells) => enableConfigRecorders(cells, scope)); }}>Enable</Button>
              <Button size="small" disabled={!canDisable} onClick={() => act(disableConfigRecorders)}>Disable</Button>
              <Button size="small" color="error" disabled={!canDelete} onClick={() => setConfirmDelete(true)}>Delete</Button>
            </Box>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.muted, fontWeight: 500, mt: 2 }}>
              AWS Config bills per configuration item recorded + S3 storage. Minimal records only the resource types your enabled standards need.
            </Typography>
          </Paper>
          </motion.div>

          <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={listCardSx}>
            <Box sx={{ px: 3, pt: 3, pb: 2 }}>
              <PortalSectionHeader
                icon={<StorageIcon sx={{ fontSize: 20 }} />}
                title="Recorders by account and region"
                subtitle="Select cells, then use the recording actions above"
                mb={0}
                action={(
                  <>
                    <Button size="small" startIcon={<AddIcon />} onClick={(e) => setAddAnchor(e.currentTarget)}>
                      Add region
                    </Button>
                    <Menu anchorEl={addAnchor} open={!!addAnchor} onClose={() => setAddAnchor(null)}>
                      {unusedRegions.map((r) => (
                        <MenuItem
                          key={r}
                          onClick={() => { setExtraRegions((p) => [...new Set([...p, r])]); setAddAnchor(null); }}
                        >
                          {r}
                        </MenuItem>
                      ))}
                      {unusedRegions.length === 0 && (
                        <MenuItem disabled>All regions already shown</MenuItem>
                      )}
                    </Menu>
                  </>
                )}
              />
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={tableHeadRowSx}>
                    <TableCell sx={stickyHeadCellSx}>Account</TableCell>
                    {displayRegions.map((rg) => <TableCell key={rg} align="center" sx={headCellSx}>{rg}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.map((acct, index) => (
                    <TableRow
                      key={acct}
                      hover
                      sx={{ ...rowSx(index), '&:hover .config-account-cell': { backgroundColor: CK.pageBg } }}
                    >
                      <TableCell className="config-account-cell" sx={stickyBodyCellSx}>{acct}</TableCell>
                      {displayRegions.map((rg) => {
                        const cell = byKey[`${acct}|${rg}`] || { status: 'none' };
                        const isExternal = cell.status === 'external';
                        const isActive = ACTIVE.includes(cell.status);
                        return (
                          <TableCell key={rg} align="center" sx={bodyCellSx}>
                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                              <Tooltip title={isExternal ? 'An existing AWS Config recorder is present and not managed by Prism' : (cell.errorMessage || '')}>
                                <Chip label={formatStatusLabel(cell.status)} size="small" sx={statusChipSx(intentFromColor(STATUS_COLOR[cell.status] || 'default'))} />
                              </Tooltip>
                              {cell.recordingScope && ['enabled', 'disabled', 'external'].includes(cell.status) && (
                                <Tooltip title={cell.recordingScope === 'all'
                                  ? 'Recording ALL supported resource types (higher AWS Config cost)'
                                  : 'Minimal — records only the resource types your enabled standards need'}>
                                  <Chip
                                    label={cell.recordingScope === 'all' ? 'all' : 'minimal'}
                                    size="small"
                                    sx={statusChipSx(cell.recordingScope === 'all' ? 'warning' : 'neutral')}
                                  />
                                </Tooltip>
                              )}
                              {isActive && <CircularProgress size={12} thickness={5} sx={{ color: CK.blue }} />}
                              {cell.unmappedWarning && <Tooltip title="Some enabled standards have unmapped controls — consider 'Record all'"><Chip label="!" size="small" sx={statusChipSx('warning')} /></Tooltip>}
                              {!isExternal && (
                                <Checkbox
                                  size="small"
                                  checked={selected.has(`${acct}|${rg}`)}
                                  onChange={() => toggle(acct, rg)}
                                />
                              )}
                            </Stack>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                  {accounts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={displayRegions.length + 1} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                        <Typography sx={emptyTextSx}>
                          No enrolled accounts yet — enroll accounts in Security Hub CSPM first.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          </motion.div>

          <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={{ ...sectionCardSx, mt: 3 }}>
            <RolePermissionsPanel
              startScan={startConfigRolesScan}
              getJob={getConfigRolesJob}
              fix={fixConfigRoles}
              capabilityLabel="AWS Config"
            />
          </Paper>
          </motion.div>
        </>
      )}

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} {...dialogProps}>
        <DialogTitle sx={dialogTitleSx}>Delete AWS Config recorders</DialogTitle>
        <DialogContent sx={{ p: 3, '&.MuiDialogContent-root': { pt: 3 } }}>
          <Typography variant="body2" sx={{ color: CK.body }}>
            This permanently deletes the AWS Config recorder in {selected.size} cell(s), and removes the
            recorded configuration history from the Prism-managed S3 bucket when it is an account&apos;s last recorder. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1, '& > :not(:first-of-type)': { ml: 0 } }}>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" onClick={() => { setConfirmDelete(false); act(deleteConfigRecorders); }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}
